import FormHeader from "@/components/FormHeader";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getListPeriode, getListUnitkerja } from "@/services/getData";
import { Periode, Unitkerja } from "@/types";
import { ChevronRight } from "lucide-react";
import React, { useState } from "react";
import { LuListFilter } from "react-icons/lu";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";

export default function FormFilter({ closeModal }: { closeModal: () => void }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const idsPeriode = searchParams.get("ids_sys_periode");
  const idsUk = searchParams.get("ids_uk");

  const [selectedFilter, setSelectedFilter] = useState("PERIODE");
  const [filter, setFilter] = useState<{
    periode: string[];
    program_studi: string[];
  }>({
    periode: idsPeriode?.split("-") || [],
    program_studi: idsUk?.split("-") || [],
  });
  const [searchQueryProdi, setSearchQueryProdi] = useState("");

  const { data: dataPeriode, isPending: isPendingDataPeriode } = useQuery({
    queryKey: ["listPeriode"],
    queryFn: () => getListPeriode(),
  });
  const listPeriodePasca = dataPeriode
    ? dataPeriode?.filter((periode: Periode) => {
        return periode.jalur_jenis?.nama_jalur_jenis_slug === "pascasarjana";
      })
    : [];

  const { data: listUnitkerja, isPending: isPendingListUnitkerja } = useQuery({
    queryKey: ["listUnitkerja"],
    queryFn: getListUnitkerja,
  });

  const listProdi = listUnitkerja?.filter((unitkerja: Unitkerja) => {
    return unitkerja.unitkerja_group_key == "5" && unitkerja.is_active;
  });

  const listProdiPasca = listProdi
    ? listProdi.filter((unitkerja: Unitkerja) => {
        return (
          unitkerja.uk_program === "S2" ||
          unitkerja.uk_program === "Sp-2" ||
          unitkerja.uk_program === "S2 Terapan" ||
          unitkerja.uk_program === "S3 Terapan" ||
          unitkerja.uk_program === "S3"
        );
      })
    : [];

  const filteredListProdi = listProdiPasca
    ? listProdiPasca.filter((prodi: Unitkerja) => {
        return prodi?.nama
          ?.toLowerCase()
          .includes(searchQueryProdi.toLowerCase());
      })
    : [];

  const handleApplyFilter = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (filter.periode.length > 0) {
      searchParams.set("ids_sys_periode", filter.periode.join("-"));
    } else {
      searchParams.delete("ids_sys_periode");
    }

    if (filter.program_studi.length > 0) {
      searchParams.set("ids_uk", filter.program_studi.join("-"));
    } else {
      searchParams.delete("ids_uk");
    }

    navigate(`/operator-pasca/data-nilai?${searchParams.toString()}`);
    closeModal();
  };

  return (
    <form
      onSubmit={handleApplyFilter}
      className="pb-20 relative flex flex-col gap-y-3"
    >
      <FormHeader title="Filter Data Rekap" icon={<LuListFilter />} />

      <section className="p-3 relative border rounded-xl max-h-[50vh] overflow-auto">
        <div
          className="flex justify-between items-center"
          onClick={() =>
            setSelectedFilter((prev) => (prev === "PERIODE" ? "" : "PERIODE"))
          }
        >
          <span className="font-bold">Periode</span>
          <Button type="button" size={"icon"} variant={"outline"}>
            <ChevronRight
              className={`${
                selectedFilter === "PERIODE" ? "rotate-90" : ""
              } duration-300`}
            />
          </Button>
        </div>
        {selectedFilter === "PERIODE" &&
          (isPendingDataPeriode ? (
            "Loading..."
          ) : (
            <>
              <div className="mt-3 grid grid-cols-1 gap-5">
                {listPeriodePasca?.map((periode: Periode) => {
                  const isChecked = (filter.periode as string[]).includes(
                    String(periode.id_sys_periode),
                  );

                  return (
                    <div key={periode.id_sys_periode} className="flex gap-3">
                      <Checkbox
                        checked={isChecked}
                        onCheckedChange={(checked) => {
                          setFilter((prev) => ({
                            ...prev,
                            periode: checked
                              ? [String(periode.id_sys_periode)] // replace dengan satu saja
                              : [], // kosongkan
                          }));
                        }}
                      />
                      <Label>
                        {periode.jalur_jenis?.nama_jalur_jenis} Gelombang-
                        {periode.gelombang_show}{" "}
                        {periode.is_internasional_kerjasama
                          ? "(KERJASAMA)"
                          : "(REGULER)"}
                      </Label>
                    </div>
                  );
                })}
              </div>
            </>
          ))}
      </section>

      <section className="p-3 relative border rounded-xl max-h-[50vh] overflow-auto">
        <div
          className="flex justify-between items-center"
          onClick={() =>
            setSelectedFilter((prev) => (prev === "PRODI" ? "" : "PRODI"))
          }
        >
          <span className="font-bold">Program Studi</span>
          <Button type="button" size={"icon"} variant={"outline"}>
            <ChevronRight
              className={`${
                selectedFilter === "PRODI" ? "rotate-90" : ""
              } duration-300`}
            />
          </Button>
        </div>
        {selectedFilter === "PRODI" &&
          (isPendingListUnitkerja ? (
            "Loading..."
          ) : (
            <>
              <div className="py-3 sticky top-0">
                <Input
                  placeholder="Search..."
                  value={searchQueryProdi}
                  onChange={(event) => setSearchQueryProdi(event.target.value)}
                />
              </div>
              <div className="mt-3 grid grid-cols-2 gap-5">
                {filteredListProdi?.map((prodi: Unitkerja) => {
                  const isChecked = (filter.program_studi as string[]).includes(
                    String(prodi.uk_id),
                  );

                  return (
                    <div key={prodi.uk_id} className="flex gap-3">
                      <Checkbox
                        checked={isChecked}
                        onCheckedChange={(checked) => {
                          setFilter((prev) => {
                            const programStudi = [...prev.program_studi];

                            if (checked) {
                              if (!programStudi.includes(String(prodi.uk_id))) {
                                programStudi.push(String(prodi.uk_id));
                              }
                            } else {
                              const index = programStudi.indexOf(
                                String(prodi.uk_id),
                              );
                              if (index > -1) {
                                programStudi.splice(index, 1);
                              }
                            }

                            return {
                              ...prev,
                              program_studi: programStudi,
                            };
                          });
                        }}
                      />
                      <Label>{prodi.nama}</Label>
                    </div>
                  );
                })}
              </div>
            </>
          ))}
      </section>

      <div className="absolute bottom-3 left-3 right-3">
        <div className="flex justify-between">
          <div className="flex gap-3">
            <Button
              onClick={() => closeModal()}
              type="button"
              variant={"outline"}
            >
              Tutup
            </Button>
            <Button>Terapkan Filter</Button>
          </div>
          <Button
            type="button"
            variant={"outline"}
            onClick={() => {
              setFilter((prev) => ({
                ...prev,
                periode: [],
              }));
              navigate("/operator-pasca/data-nilai");
            }}
          >
            Reset
          </Button>
        </div>
      </div>
    </form>
  );
}
